"use client";
import { useFloat } from "@/lib";
import {
  GH_REPOSITORY_URL,
  RT_DEMO_CAL_URL,
  RT_LOGO_BLACK_URL,
  RT_LOGO_CTA,
  RT_LOGO_URL,
  RT_STUDIO_RIL,
} from "@/data";
import Logo from "@/components/ratl/utility/logo-nav";
import { useState, useEffect } from "react";
import CustomButton from "@/components/ratl/utility/CustonButton";
import { usePathname } from "next/navigation";
import { NavbarDemo } from "@/components/ratl/navbar-new";

const sectionsOrder = ["Agents", "How it works", "Simple Steps", "Integration"];

const Navbar = () => {
  const pathname = usePathname();
  const isHomePage = pathname === "/";

  const { float: toggleNav, toggleFloat: setToggleNav } = useFloat(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isBelowIntegration, setIsBelowIntegration] = useState(false);
  const [currentSection, setCurrentSection] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { float, handleFloat } = useFloat([false, false]);

  // Update isScrolled and isBelowIntegration on scroll.
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 100);

      // Determine if we've scrolled past the "Integration" section.
      const integrationEl = document.querySelector(
        `[data-section="Integration"]`
      );
      if (integrationEl) {
        const rect = integrationEl.getBoundingClientRect();
        // If the bottom of the Integration section is above the viewport, mark as below.
        setIsBelowIntegration(rect.bottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Use IntersectionObserver to update the currentSection state.
  useEffect(() => {
    const sections = document.querySelectorAll("[data-section]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionName = (
              entry.target.getAttribute("data-section") || ""
            ).trim();
            setCurrentSection(sectionName);
          }
        });
      },
      { threshold: 0.5 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  // Close the dropdown when the current section changes.
  useEffect(() => {
    setDropdownVisible(false);
  }, [currentSection]);

  // Compute visibility: show the mobile navbar if not scrolled,
  // OR if the current section is "Agents" or beyond,
  // OR if we've scrolled past the Integration section.
  const currentIndex = sectionsOrder.findIndex(
    (section) => section.toLowerCase() === currentSection.toLowerCase()
  );
  const agentsIndex = sectionsOrder.findIndex(
    (section) => section.toLowerCase() === "agents"
  );
  const showMobileNavbar =
    !isScrolled || currentIndex >= agentsIndex || isBelowIntegration;

  // Handler for scrolling to a section.
  const scrollToSection = (sectionName: string) => {
    const el = document.querySelector(`[data-section="${sectionName}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      setDropdownVisible(false);
    }
  };

  return (
    <>
      {/* Desktop Navbar */}
      <div
        className={`hidden lg:block text-white ${
          isScrolled ? "bg-beige" : "bg-beige border-b"
        } w-screen z-50 fixed`}
      >
        <div className="flex flex-row items-center justify-between mx-5 md:mx-[120px] md:py-2">
          <div className="flex lg:justify-between justify-center items-center">
            <a className="w-full" href={RT_LOGO_CTA}>
              <Logo logoSrc={RT_LOGO_BLACK_URL} />
            </a>
          </div>

          <div className="flex flex-row gap-2 items-center">
            <nav className="">
              <ul className="flex lg:items-center font-inter font-medium lg:gap-1">
                <li className="w-fit px-3">
                  <NavbarDemo />
                </li>
                <li className="px-3">
                  <a
                    href="/about-us"
                    className="text-sm whitespace-nowrap flex items-center p-1 text-light-body-text hover:text-black"
                  >
                    About
                  </a>
                </li>
                <li className="px-3">
                  <a
                    href="/pricing"
                    className="text-sm whitespace-nowrap flex items-center p-1 text-light-body-text hover:text-black"
                  >
                    Pricing
                  </a>
                </li>
                <li className="px-3">
                  <a
                    href="/use-cases"
                    className="text-sm whitespace-nowrap flex items-center p-1 text-light-body-text hover:text-black"
                  >
                    Use Cases
                  </a>
                </li>
              </ul>
            </nav>
            <div>
              <CustomButton
                text={"Book a Demo"}
                animationRequired={false}
                bgType={"solid"}
                onClick={() => {
                  window.location.href = RT_DEMO_CAL_URL;
                }}
                onMouseEnter={() => handleFloat(true, 1)}
                onMouseLeave={() => handleFloat(false, 1)}
                controller={float[1]}
                btnSize="small"
                isStudioBtn={false}
                className="px-4 py-2 text-sm transition w-fit rounded-lg border-solid border-[1px] hover:bg-transparent hover:text-black hover:border-solid hover:border-black hover:border-[1px]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navbar */}
      <div className="lg:hidden fixed p-1 rounded-xl border-[#c8c8c8]/25 border border-solid bg-white backdrop-filter backdrop-blur-[25px] top-5 left-5 right-5">
        <div className="flex flex-row items-center justify-center h-full">
          <div className="px-2">
            <a href={RT_LOGO_CTA}>
              <Logo logoSrc={RT_LOGO_BLACK_URL} width={100} />
            </a>
          </div>
          {/* <div>
            <CustomButton
              text={"Book a Demo"}
              animationRequired={false}
              bgType={"solid"}
              onClick={() => {
                window.location.href = RT_DEMO_CAL_URL;
              }}
              onMouseEnter={() => handleFloat(true, 1)}
              onMouseLeave={() => handleFloat(false, 1)}
              controller={float[1]}
              btnSize="small"
              isStudioBtn={false}
              className="px-4 py-2 text-sm transition w-fit rounded-lg"
              showMobile={true}
            />
          </div> */}
        </div>
      </div>
    </>
  );
};

export default Navbar;
